import { Table, Typography } from "antd";
const { Text } = Typography;
import moment from "moment-timezone";
import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";

import { ApiNames } from "../../api/api";
import { SUPPORTED_COVERAGES } from "../../constant/patient";
import { PAGE_SIZE, selectedViews } from "../../constant/table";
import { TabsMap } from "../../screen/patient/Patient";
import { PageKeys } from "../../utils/pendo/useStandardizedTracking";
import ACTION from "../../store/action";
import { selectIsFetching, selectPendingRenewalData, selectTableColumns } from "../../store/selector";
import { dateFormatter } from "../../utils/date";
import { fetchTableData } from "../../utils/fetchTableData";
import { capitalizeFirstLetter } from "../../utils/string";
import { programTypeMapper } from "@tailormed/common-client/util/programTypeMapping";
import { ColumnSelector } from "../ColumnSelector";
import { patientInsuranceCell, PatientStatusCell } from "../TableCells";
import { TailormedSpinner } from "../TailormedSpinner/TailormedSpinner";
import LinkableCell from "../UI/LinkableCell";
import {
  AssigneeColumn,
  DrugColumnComponent,
  EditColumn,
  EnrollmentAssigneeColumn,
  PatientDOBColumn,
  PatientNameColumn,
  PatientPhoneNumberColumn,
  PatientSSNColumn,
  StarColumn,
  withSorting
} from "./CommonColumns";
import ConsentStatusColumn from "./ConsentStatusColumn";
import EmptyTableMessage from "./EmptyTableMessage";
import PatientPageCount from "./PatientPageCount";
import { ColumnSelectorContainer } from "./TableStyle";
import { commonBulkActionsTableStyle } from "./BulkActionsTableStyle";
import { ENROLLMENT_STATUS_COLORS, getStatusTagStyle } from "../../util/statusTagStyles";

const StyledTable = styled(Table)`
  ${(props) => commonBulkActionsTableStyle(props.isFilterListView, props.$hasSelection)}
`;

const patientPath = ({ patientId, journeyId }) => TabsMap.applications.path(patientId, journeyId);

const renderWithLink = (patientRec, renderFn) => <LinkableCell to={patientPath(patientRec)}>{renderFn()}</LinkableCell>;

const PendingRenewalTableComponent = ({
  tableColumns,
  isFetching,
  setTableDataAct,
  tableData,
  t,
  updateSavedFilterViewParameters,
  isFilterListView,
  rowSelection
}) => {
  const totalRecordsCount = tableData?.totalPatients;
  const sortingEnabled = tableData?.sortingEnabled;
  const [hoveringLineIndex, setHoveringLineIndex] = useState(null);

  const columns = [
    {
      dataIndex: tableColumns[selectedViews.pendingRenewal]?.star?.dataIndex,
      key: tableColumns[selectedViews.pendingRenewal]?.star?.key,
      width: 35,
      className: "star-column",
      render: (text, record) => <StarColumn record={record} />
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.name_id, {
      sorterTooltipText: t("columns.sortById"),
      minWidth: 300,
      render: (text, record) => renderWithLink(record, () => <PatientNameColumn record={record} />)
    }),
    {
      ...tableColumns[selectedViews.pendingRenewal]?.phone_number,
      render: (text, record) => renderWithLink(record, () => <PatientPhoneNumberColumn record={record} />)
    },
    {
      ...tableColumns[selectedViews.pendingRenewal]?.dob,
      render: (text, record) => renderWithLink(record, () => <PatientDOBColumn record={record} />)
    },
    {
      ...tableColumns[selectedViews.pendingRenewal]?.ssn,
      render: (text, record) => renderWithLink(record, () => <PatientSSNColumn record={record} />)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.assistanceProgram, {
      render: (text, record) =>
        renderWithLink(record, () => (record.assistanceProgramName ? record.assistanceProgramName : null))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.apType, {
      render: (text, record) => renderWithLink(record, () => (record.apType ? record.apType : null))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.providedBy, {
      render: (_, record) => renderWithLink(record, () => record.providedBy)
    }),
    {
      ...tableColumns[selectedViews.pendingRenewal]?.associatedDrug,
      render: (text, record) => renderWithLink(record, () => <DrugColumnComponent drugs={record.associatedDrugs} />)
    },
    {
      ...tableColumns[selectedViews.allApplications]?.enrollmentAssignee,
      render: (text, record) => renderWithLink(record, () => <EnrollmentAssigneeColumn record={record} />)
    },
    {
      ...tableColumns[selectedViews.pendingRenewal]?.assignee,
      render: (text, record) => renderWithLink(record, () => <AssigneeColumn record={record} />)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.facility, {
      render: (_, record) => renderWithLink(record, () => record.facility)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.physician, {
      render: (_, record) => renderWithLink(record, () => record.physician_full_name)
    }),
    {
      ...tableColumns[selectedViews.pendingRenewal]?.employer,
      render: (_, record) => renderWithLink(record, () => record.employer)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.creationDate, {
      render: (text, record) => renderWithLink(record, () => moment(record.creationDate).format("MMM D, YYYY"))
    }),

    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.patientStatus, {
      render: (_, record) => renderWithLink(record, () => <PatientStatusCell value={record.status} />)
    }),
    {
      ...tableColumns[selectedViews.pendingRenewal]?.primaryInsurancePayer,
      render: (_, record) => renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.primary)(_, record))
    },
    {
      ...tableColumns[selectedViews.pendingRenewal]?.secondaryInsurancePayer,
      render: (_, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.secondary)(_, record))
    },
    {
      ...tableColumns[selectedViews.pendingRenewal]?.tertiaryInsurancePayer,
      render: (_, record) => renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.tertiary)(_, record))
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.applicationStatus, {
      width: 150,
      render: (text, record) =>
        renderWithLink(record, () =>
          record.applicationStatus ? (
            <Text style={getStatusTagStyle(record.applicationStatus, ENROLLMENT_STATUS_COLORS)}>
              {capitalizeFirstLetter(record.applicationStatus)}
            </Text>
          ) : null
        )
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.fundBalance, {
      render: (text, record) =>
        renderWithLink(record, () => {
          const isValidBalance = !isNaN(parseFloat(record.fundBalance));

          let parsedSubtypes = record.programSubtypes;
          if (typeof record.programSubtypes === "string") {
            try {
              parsedSubtypes = JSON.parse(record.programSubtypes);
            } catch (e) {
              parsedSubtypes = [];
            }
          }

          const recordForClassification = {
            ...record,
            programSubtypes: parsedSubtypes
          };

          const isFreeDrugProgram = programTypeMapper?.isManufacturerFreeDrugProgram(recordForClassification);

          return isValidBalance && !isFreeDrugProgram ? `$${record.fundBalance}` : null;
        })
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.validThrough, {
      width: 130,
      render: (text, record) =>
        renderWithLink(record, () => {
          if (record.effectiveIndefinitely) {
            return t("patientApplications.indefinitely");
          } else {
            return moment(record.approvalValidThrough).isValid() && dateFormatter(record.approvalValidThrough);
          }
        })
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.enrollmentCreationDate, {
      render: (text, record) =>
        renderWithLink(
          record,
          () => moment(record.enrollmentCreationDate).isValid() && dateFormatter(record.enrollmentCreationDate)
        )
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.appliedDate, {
      render: (text, record) => renderWithLink(record, () => record.appliedDate && dateFormatter(record.appliedDate))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.approvedDate, {
      render: (text, record) => renderWithLink(record, () => record.approvedDate && dateFormatter(record.approvedDate))
    }),
    {
      ...tableColumns[selectedViews.pendingRenewal]?.agreementSignatureDate,
      render: (text, record) =>
        renderWithLink(record, () => record.activeConsentDate && dateFormatter(record.activeConsentDate))
    },
    {
      ...tableColumns[selectedViews.pendingRenewal]?.consentStatus,
      render: (text, record) => renderWithLink(record, () => <ConsentStatusColumn record={record} />)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.followupDate, {
      render: (text, record) => renderWithLink(record, () => record.followupDate && dateFormatter(record.followupDate))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.pendingRenewal]?.latestClaimDate, {
      render: (text, record) =>
        renderWithLink(record, () => record.latestClaimDate && dateFormatter(record.latestClaimDate))
    }),
    {
      dataIndex: tableColumns[selectedViews.pendingRenewal]?.edit?.dataIndex,
      key: tableColumns[selectedViews.pendingRenewal]?.edit?.key,
      width: 80,
      className: "action-column",
      render: (_, record, rowIndex) => (
        <EditColumn
          isHovering={hoveringLineIndex === rowIndex}
          record={record}
          isFilterListView={isFilterListView}
          pageKey={PageKeys.PENDING_RENEWALS}
        />
      )
    }
  ];
  return (
    <>
      {!!tableData?.patients.length && (
        <ColumnSelectorContainer>
          <ColumnSelector />
        </ColumnSelectorContainer>
      )}
      <StyledTable
        size="small"
        rowClassName={(_, rowIndex) => `cursor-pointer${hoveringLineIndex === rowIndex ? " row-hovered" : ""}`}
        style={{ width: "100%" }}
        $hasSelection={rowSelection?.selectedRowKeys?.length > 0}
        rowKey="applicationId"
        rowSelection={rowSelection}
        loading={{ indicator: <TailormedSpinner />, spinning: isFetching }}
        columns={columns.filter((col) => tableColumns[selectedViews.pendingRenewal]?.[col.key]?.display)}
        locale={{
          emptyText: <EmptyTableMessage type="enrollments" />
        }}
        onRow={(record, rowIndex) => {
          return {
            "data-pendo": "pending_renewal_row",
            "data-journey-id": record.journeyId,
            onMouseEnter: () => setHoveringLineIndex(rowIndex),
            onMouseLeave: () => setHoveringLineIndex(null)
          };
        }}
        onChange={(pagination, filters, sorter) =>
          fetchTableData(pagination, sorter, tableData, setTableDataAct, updateSavedFilterViewParameters)
        }
        isFilterListView={isFilterListView}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: tableData && tableData.selectedPage,
          defaultPageSize: PAGE_SIZE,
          pageSize: PAGE_SIZE,
          position: ["bottom"],
          total: totalRecordsCount,
          showTotal: (total, range) => (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <PatientPageCount
                total={total}
                range={range}
                isAccurateCount={tableData.isAccurateCount}
                isEnrollment={true}
              />
            </div>
          )
        }}
        dataSource={
          tableData && tableData.patients
            ? tableData.patients
                .map((patient, index) => {
                  return {
                    ...patient,
                    key: index
                  };
                })
                .sort((a, b) => b.isActive - a.isActive)
            : null
        }
      />
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  tableColumns: selectTableColumns,
  tableData: selectPendingRenewalData,
  isFetching: selectIsFetching([ApiNames.PendingRenewalPhiSearch, ApiNames.PendingRenewalSearch])
});

const mapDispatchToProps = (dispatch) => ({
  setTableDataAct: (page, sorter) => dispatch(ACTION.paginateSortChange(page, selectedViews.pendingRenewal, sorter))
});

const PendingRenewalTable = compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(PendingRenewalTableComponent);

export { PendingRenewalTable };
